import { Box } from '@mui/material';
import type { SxProps } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { useDragLayer } from 'react-dnd';
import { DraggableItem } from '../DraggableItem/DraggableItem';

const List = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

interface Props<T extends { id: string }> {
  sx?: SxProps;
  items: T[];
  renderItem: (item: T, dragHandleRef?: React.Ref<HTMLElement>) => React.ReactNode;
  onChange: (items: T[]) => void;
}

// Renders a position:fixed preview that follows the cursor, bypassing the
// overflow:hidden/auto ancestors that cause Safari to produce a blank native ghost.
function DragLayerPreview<T extends { id: string }>({
  items,
  renderItem,
}: Readonly<{
  items: T[];
  renderItem: (item: T) => React.ReactNode;
}>) {
  const { isDragging, draggedId, currentOffset, width } = useDragLayer(
    (monitor) => {
      const item = monitor.getItem<{ id: string; width: number }>();
      return {
        isDragging: monitor.isDragging(),
        draggedId: item?.id,
        currentOffset: monitor.getSourceClientOffset(),
        width: item?.width ?? 0,
      };
    },
  );

  if (!isDragging || !currentOffset || !draggedId) return null;

  const draggedItem = items.find((i) => i.id === draggedId);
  if (!draggedItem) return null;

  return (
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width,
        transform: `translate(${currentOffset.x}px, ${currentOffset.y}px)`,
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    >
      {renderItem(draggedItem)}
    </Box>
  );
}

export function DraggableList<T extends { id: string }>({
  sx,
  items,
  renderItem,
  onChange,
}: Props<T>) {
  const [acceptSymbol] = useState(`draggableItem${Math.random()}`);
  const [localItems, setLocalItems] = useState(items);
  const currentItemsRef = useRef(items);

  useEffect(() => {
    currentItemsRef.current = items;
    setLocalItems(items);
  }, [items]);

  const moveCard = (dragIndex: number, hoverIndex: number) => {
    const newItems = [...currentItemsRef.current];
    const [removed] = newItems.splice(dragIndex, 1);
    newItems.splice(hoverIndex, 0, removed);
    currentItemsRef.current = newItems;
    setLocalItems(newItems);
  };

  const handleDrop = () => {
    onChange(currentItemsRef.current);
  };

  return (
    <>
      <DragLayerPreview items={localItems} renderItem={renderItem} />
      <List sx={sx}>
        {localItems.map((item, index) => (
          <DraggableItem
            key={item.id}
            acceptSymbol={acceptSymbol}
            index={index}
            id={item.id}
            moveCard={moveCard}
            onDrop={handleDrop}
            renderContent={(dragHandleRef) => renderItem(item, dragHandleRef)}
          />
        ))}
      </List>
    </>
  );
}
